// ActivityDashboard.jsx
// Owner-only view of admin/team activity
// Shows: session history, active hours, page breakdown, action counts
//
// In CRM: case "activity": return <ActivityDashboard currentUser={currentUser} />

import React, { useState, useEffect } from 'react';

const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;

const PAGE_LABELS = {
  dashboard: 'Dashboard', leads: 'Leads', calls: 'Call Tracking', jobs: 'Job Tracking',
  customers: 'Customer Database', calendar: 'Calendar', estimates: 'Estimates',
  invoicing: 'Invoicing', reviews: 'Reviews', gallery: 'Photo Gallery', marketing: 'Marketing',
  outreach: 'Outreach Drafts', aiassistant: 'AI Assistant', materials: 'Materials',
  schedule: 'Crew App', training: 'Training', reports: 'Reports', users: 'User Management',
  playbook: 'Playbook', catalog: 'Materials Catalog', budgets: 'Budget Builder',
  pldash: 'P&L Dashboard', automations: 'automations',
};

const ACTION_LABELS = {
  lead_updated: 'Leads updated', lead_created: 'Leads created', call_made: 'Calls made',
  sms_sent: 'SMS sent', estimate_created: 'Estimates created', estimate_sent: 'Estimates sent',
  job_created: 'Jobs created', job_updated: 'Jobs updated', invoice_created: 'Invoices created',
  review_requested: 'Reviews requested', note_added: 'Notes added', photo_uploaded: 'Photos uploaded',
};

const ACTION_COLORS = {
  lead_updated: '#3b5bdb', lead_created: '#3b5bdb', call_made: '#946c00',
  sms_sent: '#946c00', estimate_created: '#1a6b2a', estimate_sent: '#1a6b2a',
  job_created: '#c9a96e', job_updated: '#c9a96e', invoice_created: '#5a5248',
  review_requested: '#c9a96e', note_added: '#8c7b6e', photo_uploaded: '#8c7b6e',
};

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}
function fmtDay(dateStr) {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtDur(min) {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const ActivityDashboard = ({ currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userData, setUserData] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/activity/track?all=true&days=${period}`);
      const json = await res.json();
      setData(json);
    } catch (e) { console.log('Activity fetch error:', e.message); }
    setLoading(false);
  };

  const fetchUser = async (userId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/activity/track?userId=${userId}&week=true`);
      const json = await res.json();
      setUserData(json);
    } catch (e) { console.log('User activity error:', e.message); }
  };

  useEffect(() => { fetchAll(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedUser) fetchUser(selectedUser.userId);
  }, [selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  if (currentUser?.role !== 'owner') {
    return (
      <div className="fade-in" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <div style={{ fontWeight: 600, color: 'var(--stone-500)' }}>Owner access only</div>
        <div style={{ fontSize: 13, color: 'var(--stone-400)', marginTop: 4 }}>Activity tracking data is restricted to the business owner.</div>
      </div>
    );
  }

  const cardStyle = { background: 'white', borderRadius: 'var(--radius-lg, 12px)', padding: '20px 24px', marginBottom: 14, border: '1px solid var(--stone-200)' };

  // ── Group sessions by day for the detail view ──
  const groupSessionsByDay = (sessions) => {
    const byDay = {};
    for (const s of (sessions || [])) {
      const day = s.started_at.slice(0, 10);
      if (!byDay[day]) byDay[day] = { sessions: [], totalMinutes: 0 };
      const dur = s.active_minutes || 0;
      byDay[day].sessions.push(s);
      byDay[day].totalMinutes += dur;
    }
    return Object.entries(byDay).sort(([a], [b]) => b.localeCompare(a)); // newest first
  };

  return (
    <div className="fade-in" style={{ minHeight: 'calc(100vh - 120px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="section-title">Team Activity</h1>
          <p className="section-sub">Session tracking, page visits, and action counts for your admin team.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={period} onChange={e => setPeriod(Number(e.target.value))} style={{ width: 'auto', padding: '6px 12px', fontSize: 13 }}>
            <option value={1}>Today</option>
            <option value={7}>Last 7 Days</option>
            <option value={14}>Last 14 Days</option>
            <option value={30}>Last 30 Days</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={fetchAll}>
            {loading ? 'Loading...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--stone-400)', fontSize: 13 }}>Loading activity data...</div>
      ) : !data?.users?.length ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
          <div style={{ fontWeight: 600, color: 'var(--stone-500)' }}>No activity data yet</div>
          <div style={{ fontSize: 13, color: 'var(--stone-400)', marginTop: 4 }}>Activity will appear here when admin users log in to the CRM.</div>
        </div>
      ) : selectedUser ? (
        /* ═══ USER DETAIL VIEW ═══ */
        <div>
          <button onClick={() => { setSelectedUser(null); setUserData(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--stone-500)', fontSize: 13, fontWeight: 600, marginBottom: 16, padding: 0 }}>
            ← Back to Team Overview
          </button>

          {/* User header */}
          <div style={{ ...cardStyle, borderLeft: '4px solid var(--accent-blue)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700 }}>{selectedUser.userName}</div>
                <div style={{ fontSize: 13, color: 'var(--stone-500)' }}>{selectedUser.role} · {selectedUser.sessions} sessions · {selectedUser.totalFormatted} total</div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent-blue)' }}>{selectedUser.totalFormatted}</div>
            </div>
          </div>

          {userData && (
            <>
              {/* ── Daily Breakdown (card per day) ── */}
              {Object.keys(userData.byDay || {}).length > 0 && (
                <div style={cardStyle}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Daily Breakdown</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {Object.entries(userData.byDay).sort(([a], [b]) => a.localeCompare(b)).map(([day, info]) => {
                      const hours = Math.round(info.minutes / 60 * 10) / 10;
                      return (
                        <div key={day} style={{ flex: '1 1 100px', minWidth: 90, background: 'var(--stone-100)', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: hours >= 4 ? 'var(--accent-green)' : hours >= 2 ? 'var(--accent-blue)' : 'var(--accent-amber)' }}>{hours}h</div>
                          <div style={{ fontSize: 11, color: 'var(--stone-400)', marginTop: 2 }}>{fmtDay(day)}</div>
                          <div style={{ fontSize: 10, color: 'var(--stone-400)' }}>{info.sessions} session{info.sessions !== 1 ? 's' : ''}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Pages Visited ── */}
              {Object.keys(userData.pages || {}).length > 0 && (
                <div style={cardStyle}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Pages Visited</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {Object.entries(userData.pages).sort(([,a], [,b]) => b - a).map(([pg, count]) => {
                      const maxCount = Math.max(...Object.values(userData.pages));
                      return (
                        <div key={pg} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 130, fontSize: 13, fontWeight: 500, color: 'var(--stone-700)' }}>{PAGE_LABELS[pg] || pg}</div>
                          <div style={{ flex: 1, height: 20, background: 'var(--stone-100)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(count / maxCount) * 100}%`, background: 'var(--accent-blue)', borderRadius: 4, minWidth: 4, transition: 'width 0.3s' }} />
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--stone-500)', minWidth: 30, textAlign: 'right' }}>{count}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Actions Performed ── */}
              {Object.keys(userData.actions || {}).length > 0 && (
                <div style={cardStyle}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Actions Performed</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                    {Object.entries(userData.actions).sort(([,a], [,b]) => b - a).map(([act, count]) => (
                      <div key={act} style={{ background: 'var(--stone-50)', borderRadius: 8, padding: '12px 16px', borderLeft: `3px solid ${ACTION_COLORS[act] || '#8c7b6e'}` }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: ACTION_COLORS[act] || 'var(--stone-700)' }}>{count}</div>
                        <div style={{ fontSize: 11, color: 'var(--stone-500)', marginTop: 2 }}>{ACTION_LABELS[act] || act}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ═══ SESSION HISTORY — Grouped by Day ═══ */}
              <div style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>Session History</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {groupSessionsByDay(userData.sessions).map(([day, dayData]) => {
                    const dayLabel = fmtDay(day);
                    const isToday = day === new Date().toISOString().slice(0, 10);
                    const totalH = Math.round(dayData.totalMinutes / 60 * 10) / 10;
                    const sessionCount = dayData.sessions.length;

                    return (
                      <div key={day}>
                        {/* Day header row */}
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '8px 0', borderBottom: '2px solid var(--stone-200)', marginBottom: 8,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: isToday ? 'var(--accent-green)' : 'var(--stone-700)' }}>
                              {isToday ? '● Today' : dayLabel}
                            </span>
                            <span style={{ fontSize: 12, color: 'var(--stone-400)', fontWeight: 500 }}>
                              {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <span style={{
                            fontSize: 15, fontWeight: 700,
                            color: totalH >= 4 ? 'var(--accent-green)' : totalH >= 1 ? 'var(--accent-blue)' : 'var(--stone-500)',
                          }}>
                            {totalH}h active
                          </span>
                        </div>

                        {/* Sessions table for this day */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0', fontSize: 13 }}>
                          {/* Table header */}
                          <div style={{ fontSize: 11, color: 'var(--stone-400)', fontWeight: 600, padding: '4px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Time</div>
                          <div style={{ fontSize: 11, color: 'var(--stone-400)', fontWeight: 600, padding: '4px 12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Duration</div>
                          <div style={{ fontSize: 11, color: 'var(--stone-400)', fontWeight: 600, padding: '4px 0', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right', minWidth: 60 }}>Status</div>

                          {dayData.sessions.map((s, i) => {
                            const isLive = !s.ended_at;
                            const dur = s.active_minutes || 0;
                            const bg = i % 2 === 0 ? 'transparent' : 'var(--stone-50)';

                            return (
                              <React.Fragment key={s.id || i}>
                                {/* Time range */}
                                <div style={{ padding: '7px 0', background: bg, color: 'var(--stone-700)' }}>
                                  {fmtTime(s.started_at)}
                                  {s.ended_at ? ` → ${fmtTime(s.ended_at)}` : ''}
                                </div>
                                {/* Duration */}
                                <div style={{ padding: '7px 12px', background: bg, textAlign: 'right', fontWeight: 600, color: isLive ? 'var(--accent-green)' : 'var(--stone-600)' }}>
                                  {isLive ? '—' : fmtDur(dur)}
                                </div>
                                {/* Status */}
                                <div style={{ padding: '7px 0', background: bg, textAlign: 'right' }}>
                                  {isLive ? (
                                    <span style={{
                                      fontSize: 10, fontWeight: 700, color: 'var(--accent-green)',
                                      background: 'rgba(26,107,42,0.1)', padding: '2px 8px', borderRadius: 99,
                                    }}>
                                      LIVE{s.current_page ? ` · ${PAGE_LABELS[s.current_page] || s.current_page}` : ''}
                                    </span>
                                  ) : s.idle_minutes > 5 ? (
                                    <span style={{ fontSize: 11, color: 'var(--stone-400)' }}>{s.idle_minutes}m idle</span>
                                  ) : (
                                    <span style={{ fontSize: 11, color: 'var(--stone-400)' }}>
                                      {s.end_reason === 'tab_close' ? 'tab closed' : s.end_reason === 'new_session' ? 'new session' : ''}
                                    </span>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {(!userData.sessions || userData.sessions.length === 0) && (
                    <div style={{ textAlign: 'center', color: 'var(--stone-400)', fontSize: 13, padding: 20 }}>No sessions recorded this period.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        /* ═══ TEAM OVERVIEW ═══ */
        <div>
          {/* Summary stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              ['Team Members', data.users.length, 'var(--stone-700)'],
              ['Total Hours', Math.round(data.users.reduce((s, u) => s + u.totalMinutes, 0) / 60 * 10) / 10 + 'h', 'var(--accent-blue)'],
              ['Total Sessions', data.users.reduce((s, u) => s + u.sessions, 0), 'var(--warm-gold)'],
              ['Active Now', data.users.filter(u => u.sessionList.some(s => !s.ended_at)).length, 'var(--accent-green)'],
            ].map(([label, val, color]) => (
              <div key={label} className="stat-card">
                <div className="stat-value" style={{ color }}>{val}</div>
                <div className="stat-label">{label}</div>
              </div>
            ))}
          </div>

          {/* User cards */}
          <div style={{ display: 'grid', gap: 12 }}>
            {data.users.map(user => {
              const isActive = user.sessionList.some(s => !s.ended_at);
              const liveSession = user.sessionList.find(s => !s.ended_at);
              const topPages = Object.entries(user.pages).sort(([,a], [,b]) => b - a).slice(0, 3);
              const topActions = Object.entries(user.actions).sort(([,a], [,b]) => b - a).slice(0, 4);
              const totalActions = Object.values(user.actions).reduce((s, v) => s + v, 0);

              return (
                <div key={user.userId} onClick={() => setSelectedUser(user)}
                  style={{ ...cardStyle, cursor: 'pointer', transition: 'all 0.15s', borderLeft: isActive ? '4px solid var(--accent-green)' : '4px solid var(--stone-200)' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{user.userName}</div>
                      <div style={{ fontSize: 12, color: 'var(--stone-400)', marginTop: 2 }}>
                        {user.role} · {user.sessions} sessions · Avg {user.avgPerSession}/session
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent-blue)' }}>{user.totalFormatted}</div>
                      {isActive && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent-green)', background: 'rgba(26,107,42,0.1)', padding: '2px 8px', borderRadius: 99 }}>
                          ONLINE{liveSession?.current_page ? ` · ${PAGE_LABELS[liveSession.current_page] || liveSession.current_page}` : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick stats row */}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--stone-500)' }}>
                    {topPages.length > 0 && (
                      <span>Top: {topPages.map(([p]) => PAGE_LABELS[p] || p).join(', ')}</span>
                    )}
                    {totalActions > 0 && (
                      <span>{totalActions} actions</span>
                    )}
                    {topActions.length > 0 && (
                      <span>{topActions.map(([a, c]) => `${c} ${ACTION_LABELS[a] || a}`).join(' · ')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityDashboard;
